import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  motion,
  MotionConfig,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  FiArrowRight,
  FiArrowUpRight,
  FiCheck,
  FiCode,
  FiCompass,
  FiFolder,
  FiImage,
  FiLayers,
  FiMessageCircle,
  FiMove,
  FiPlay,
  FiRefreshCw,
  FiStar,
  FiTerminal,
  FiUser,
} from 'react-icons/fi';
import './home-page-premium.css';
import './home-technology.css';

const SHOWCASE_ENDPOINT = '/my-page/api/public/projects/showcase';
const FREE_TRIAL_APPLICATION_URL = '/apply/#public-event-apply';
const SHOWCASE_PROJECT_LIMIT = 10;
const MINIMUM_GALLERY_CARDS = 6;
const SHOWCASE_SECONDS_PER_CARD = 7;
const SHOWCASE_DRAG_THRESHOLD = 7;
const SHOWCASE_CLICK_SUPPRESSION_MS = 400;
const SHOWCASE_NATIVE_AUTOPLAY_PX_PER_SECOND = 48;
const SHOWCASE_NATIVE_RESUME_DELAY_MS = 700;
const SHOWCASE_NATIVE_SCROLL_IDLE_MS = 180;
const COARSE_POINTER_QUERY = '(hover: none) and (pointer: coarse)';
const COMPACT_LAYOUT_QUERY = '(max-width: 900px)';
const SHORT_OUTCOME_LAYOUT_QUERY = '(min-width: 901px) and (max-height: 880px)';

const experienceCards = [
  {
    number: '01',
    eyebrow: 'QUESTION',
    icon: FiCompass,
    title: '질문을 찾습니다',
    copy: '바꾸고 싶은 불편과 관심에서 아이가 직접 해결할 문제를 정합니다.',
    meta: '문제 발견 · 관찰 · 선택',
  },
  {
    number: '02',
    eyebrow: 'BUILD',
    icon: FiCode,
    title: 'AI와 만듭니다',
    copy: '화면과 기능을 설계하고, AI와 대화하며 작동하는 프로그램을 완성합니다.',
    meta: '기획 · 디자인 · 개발',
  },
  {
    number: '03',
    eyebrow: 'LAUNCH',
    icon: FiArrowUpRight,
    title: '실제로 공개합니다',
    copy: '링크로 공유하고, 사용자의 반응을 보며 프로그램을 다시 개선합니다.',
    meta: '배포 · 피드백 · 개선',
  },
];

const technologyFeatures = [
  {
    number: '01',
    eyebrow: 'START SIMPLE',
    icon: FiFolder,
    title: '폴더만 고르면 준비 끝',
    copy: '탐색기에서 만들고 싶은 프로젝트 폴더를 고른 뒤, 평소 말하듯 AI에게 요청합니다.',
  },
  {
    number: '02',
    eyebrow: 'STAY IN FLOW',
    icon: FiLayers,
    title: '대화는 이어지고, 작업은 나란히',
    copy: 'Resume으로 이전 대화를 잇고 AI Dock에서 다른 아이디어도 동시에 실험할 수 있습니다.',
  },
  {
    number: '03',
    eyebrow: 'GO LIVE',
    icon: FiPlay,
    title: '완성하면 실제 링크로',
    copy: '만든 프로그램을 실행해 직접 확인하고 My Page 작품전시관으로 바로 공유합니다.',
  },
];

const observableChanges = [
  {
    number: '01',
    eyebrow: 'DEFINE',
    title: '문제를 자기 언어로 설명합니다.',
    copy: '왜 필요한지, 누구에게 필요한지 스스로 문장으로 정리합니다.',
  },
  {
    number: '02',
    eyebrow: 'REVISE',
    title: '피드백을 반영해 다시 고칩니다.',
    copy: '첫 답에 머물지 않고 사용자 반응을 근거로 더 나은 선택을 합니다.',
  },
  {
    number: '03',
    eyebrow: 'SHARE',
    title: '만든 것을 실제 사용자와 공유합니다.',
    copy: '완성한 프로그램을 링크로 열고, 실제 반응 속에서 자신감을 얻습니다.',
  },
];

const observableListVariants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.18,
      staggerChildren: 0.09,
    },
  },
};

const observableItemVariants = {
  hidden: {
    opacity: 0,
    y: 36,
    scale: 0.96,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.62,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const heroCopyVariants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.08,
      staggerChildren: 0.09,
    },
  },
};

const heroCopyItemVariants = {
  hidden: {
    opacity: 0,
    y: 28,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.72,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const useMediaQuery = (query) => {
  const getMatches = () => (
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(query).matches
  );
  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(query);
    const updateMatches = (event) => setMatches(event.matches);

    setMatches(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateMatches);
    } else {
      mediaQuery.addListener?.(updateMatches);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', updateMatches);
      } else {
        mediaQuery.removeListener?.(updateMatches);
      }
    };
  }, [query]);

  return matches;
};

const useCompactLayout = () => useMediaQuery(COMPACT_LAYOUT_QUERY);

const safeHttpsUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return null;

  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
};

const safeThumbnailUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return null;
  const normalized = value.trim();

  if (/^\/my-page\/uploads\/[A-Za-z0-9][A-Za-z0-9._-]{0,254}$/.test(normalized)) {
    return normalized;
  }

  return safeHttpsUrl(normalized);
};

const normalizeProjects = (payload) => {
  const rawProjects = Array.isArray(payload)
    ? payload
    : (Array.isArray(payload?.projects) ? payload.projects : []);

  return rawProjects.reduce((projects, project) => {
    if (!project || project.is_live !== true) return projects;

    const launchUrl = safeHttpsUrl(project.launch_url);
    if (!launchUrl) return projects;

    projects.push({
      ...project,
      launch_url: launchUrl,
      thumbnail_url: safeThumbnailUrl(project.thumbnail_url),
    });
    return projects;
  }, [])
    .sort((left, right) => {
      const leftTime = Date.parse(left.showcase_added_at || '') || 0;
      const rightTime = Date.parse(right.showcase_added_at || '') || 0;
      return rightTime - leftTime;
    })
    .slice(0, SHOWCASE_PROJECT_LIMIT);
};

const JourneyCard = ({
  card,
  index,
  progress,
  staticLayout,
}) => {
  const Icon = card.icon;
  const endX = [-370, 0, 370][index] ?? 0;
  const startY = [18, 52, 86][index] ?? 0;
  const startRotate = [-8, 1, 10][index] ?? 0;
  const startScale = [0.96, 0.88, 0.78][index] ?? 1;
  const dealStart = [0.06, 0.16, 0.24][index] ?? 0.06;
  const dealEnd = [0.3, 0.4, 0.48][index] ?? 0.36;
  const focusPeak = 0.42 + (index * 0.16);

  const x = useTransform(progress, [0, dealStart, dealEnd, 1], [0, 0, endX, endX]);
  const y = useTransform(
    progress,
    [0, dealStart, dealEnd, focusPeak - 0.04, focusPeak, focusPeak + 0.1, 1],
    [startY, startY, 0, 0, -18, 0, 0],
  );
  const rotate = useTransform(
    progress,
    [0, dealStart, dealEnd, 1],
    [startRotate, startRotate, 0, 0],
  );
  const scale = useTransform(
    progress,
    [0, dealStart, dealEnd, focusPeak, focusPeak + 0.1, 1],
    [startScale, startScale, 1, 1.045, 1, 1],
  );
  const artScale = useTransform(
    progress,
    [Math.max(0, focusPeak - 0.16), focusPeak, focusPeak + 0.1],
    [0.94, 1.035, 1],
  );

  return (
    <motion.article
      className={`apple-journey-card apple-journey-card--${index + 1}`}
      data-motion-card="journey"
      style={staticLayout ? undefined : {
        x,
        y,
        rotate,
        scale,
        zIndex: 3 - index,
      }}
      initial={staticLayout ? { opacity: 0, y: 34, scale: 0.96 } : false}
      whileInView={staticLayout ? { opacity: 1, y: 0, scale: 1 } : undefined}
      viewport={staticLayout ? { once: true, amount: 0.28 } : undefined}
      transition={staticLayout ? {
        duration: 0.7,
        delay: index * 0.08,
        ease: [0.22, 1, 0.36, 1],
      } : undefined}
    >
      <div className="apple-journey-card__top">
        <span>{card.number}</span>
        <Icon aria-hidden="true" />
      </div>

      <motion.div
        className={`apple-journey-card__art apple-journey-card__art--${index + 1}`}
        aria-hidden="true"
        style={staticLayout ? undefined : { scale: artScale }}
      >
        <span />
        <span />
        <span />
        <i />
      </motion.div>

      <div className="apple-journey-card__copy">
        <small>{card.eyebrow}</small>
        <h3>{card.title}</h3>
        <p>{card.copy}</p>
        <strong>{card.meta}</strong>
      </div>
    </motion.article>
  );
};

const JourneyConnectorNode = ({ index, progress }) => {
  const activation = 0.2 + (index * 0.19);
  const opacity = useTransform(
    progress,
    [Math.max(0, activation - 0.05), activation, activation + 0.08],
    [0.25, 0.25, 1],
  );
  const scale = useTransform(
    progress,
    [Math.max(0, activation - 0.05), activation + 0.04, activation + 0.11],
    [0.65, 1.35, 1],
  );

  return (
    <motion.i style={{ opacity, scale }}>
      <span>{String(index + 1).padStart(2, '0')}</span>
    </motion.i>
  );
};

const OutcomeCard = ({
  change,
  index,
  progress,
  staticLayout,
}) => {
  const enterStart = [0.095, 0.39, 0.66][index] ?? 0.095;
  const enterEnd = [0.31, 0.56, 0.84][index] ?? 0.31;
  const focusStart = [0.16, 0.45, 0.73][index] ?? 0.16;
  const focusEnd = [0.34, 0.62, 0.91][index] ?? 0.34;
  const exitEnd = [0.47, 0.75, 1][index] ?? 0.47;
  const focusMid = (focusStart + focusEnd) / 2;
  const revealFrom = 'inset(-24% 100% -24% 0 round 29px)';
  const cardClipPath = useTransform(
    progress,
    [enterStart, enterEnd],
    [
      revealFrom,
      'inset(-24% -18% -24% -18% round 29px)',
    ],
  );
  const cardOpacity = useTransform(
    progress,
    [enterStart, Math.min(enterStart + 0.045, enterEnd), enterEnd],
    [0, 0.72, 1],
  );
  const cardX = useTransform(
    progress,
    [enterStart, enterEnd],
    [32, 0],
  );
  const cardY = useTransform(
    progress,
    [enterStart, enterEnd],
    [18, 0],
  );
  const cardScale = useTransform(
    progress,
    [enterStart, enterEnd],
    [0.965, 1],
  );
  const cardFilter = useTransform(
    progress,
    [enterStart, enterEnd],
    [
      'blur(7px) brightness(0.98) saturate(0.86)',
      'blur(0px) brightness(1) saturate(1)',
    ],
  );
  const artifactClipPath = useTransform(
    progress,
    [enterStart, enterEnd, focusEnd],
    [
      'inset(0 48% 0 0 round 20px)',
      'inset(0 0% 0 0 round 20px)',
      'inset(0 0% 0 0 round 20px)',
    ],
  );
  const artifactX = useTransform(
    progress,
    [enterStart, enterEnd, focusEnd],
    [22, 0, 0],
  );
  const artifactScale = useTransform(
    progress,
    [enterStart, focusStart, focusMid, focusEnd],
    [0.94, 1, 1.025, 1],
  );
  const artifactOpacity = useTransform(
    progress,
    [enterStart, enterEnd, focusEnd],
    [0.5, 1, 1],
  );
  const scanLeft = useTransform(
    progress,
    [focusStart, focusStart + 0.025, focusEnd - 0.025, focusEnd],
    ['-34%', '-34%', '116%', '116%'],
  );
  const scanOpacity = useTransform(
    progress,
    [focusStart, focusStart + 0.025, focusEnd - 0.025, focusEnd],
    [0, 0.9, 0.82, 0],
  );
  const glowOpacity = useTransform(
    progress,
    [enterStart, focusStart, focusMid, focusEnd, exitEnd],
    [0, 0.28, 1, 0.24, 0],
  );

  return (
    <motion.article
      className={`apple-thinking-moment apple-thinking-moment--${change.eyebrow.toLowerCase()}`}
      data-motion-card="outcome"
      data-reveal-direction="left-to-right"
      data-scroll-reveal="card-wipe"
      role="listitem"
      style={staticLayout ? undefined : {
        clipPath: cardClipPath,
        opacity: cardOpacity,
        x: cardX,
        y: cardY,
        scale: cardScale,
        filter: cardFilter,
      }}
      initial={staticLayout ? { opacity: 0, y: 36, scale: 0.96 } : false}
      whileInView={staticLayout ? { opacity: 1, y: 0, scale: 1 } : undefined}
      viewport={staticLayout ? { once: true, amount: 0.24 } : undefined}
      transition={staticLayout ? {
        duration: 0.66,
        delay: index * 0.08,
        ease: [0.22, 1, 0.36, 1],
      } : undefined}
    >
      <motion.span
        className="apple-thinking-moment__glow"
        aria-hidden="true"
        style={staticLayout ? undefined : { opacity: glowOpacity }}
      />
      <motion.span
        className="apple-thinking-moment__scan"
        aria-hidden="true"
        style={staticLayout ? undefined : { left: scanLeft, opacity: scanOpacity }}
      />

      <header>
        <span>{change.number}</span>
        <small>{change.eyebrow}</small>
      </header>

      <motion.div
        style={staticLayout ? undefined : {
          clipPath: artifactClipPath,
          x: artifactX,
          scale: artifactScale,
          opacity: artifactOpacity,
        }}
      >
        {index === 0 && (
          <div className="apple-define-quote" aria-hidden="true">
            <FiMessageCircle />
            <p>“누구의 어떤 불편을<br />바꾸고 싶은 걸까?”</p>
            <span>아이의 말로 질문을 선명하게</span>
          </div>
        )}

        {index === 1 && (
          <div className="apple-revision-diff" aria-hidden="true">
            <div>
              <small>V1</small>
              <del>기능을 더 추가해요.</del>
            </div>
            <i />
            <div>
              <small>V2</small>
              <strong>친구가 먼저 이해하게 바꿔요.</strong>
            </div>
          </div>
        )}

        {index === 2 && (
          <div className="apple-live-artifact" aria-hidden="true">
            <header>
              <span><i /><i /><i /></span>
              <small>narnialab.app/project</small>
            </header>
            <div>
              <span><i /> LIVE</span>
              <strong>첫 사용자가<br />들어왔어요.</strong>
              <p><i>J</i><i>M</i><i>S</i><b>+12</b></p>
            </div>
          </div>
        )}
      </motion.div>

      <h3>{change.title}</h3>
      <p>{change.copy}</p>
    </motion.article>
  );
};

const WorkCard = ({
  project,
  index,
  isDuplicate = false,
}) => {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = project.thumbnail_url && !imageFailed;

  useEffect(() => {
    setImageFailed(false);
  }, [project.thumbnail_url]);

  return (
    <article
      className="apple-work-card"
      role={isDuplicate ? undefined : 'listitem'}
      aria-hidden={isDuplicate || undefined}
      data-motion-card="showcase"
      data-project-position={index + 1}
      data-gallery-copy={isDuplicate ? 'duplicate' : 'primary'}
    >
      <a
        className="apple-work-link"
        href={project.launch_url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${project.title || '학생 작품'} 새 창에서 체험하기`}
        tabIndex={isDuplicate ? -1 : undefined}
        draggable="false"
      >
        <div className={`apple-work-media apple-work-media--${(index % 3) + 1}`}>
          {showImage ? (
            <img
              src={project.thumbnail_url}
              alt={isDuplicate ? '' : `${project.title || '학생 작품'} 미리보기`}
              draggable="false"
              loading="lazy"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <div className="apple-work-fallback" aria-hidden="true">
              <FiStar />
              <span>MADE BY STUDENT</span>
            </div>
          )}
        </div>
        <div className="apple-work-copy">
          <span className="apple-work-index">PROJECT {String(index + 1).padStart(2, '0')}</span>
          <h3>{project.title || '학생이 만든 새로운 프로그램'}</h3>
          <div className="apple-work-bottom">
            <span className="apple-work-creator">
              <FiUser aria-hidden="true" />
              {project.creator || '나니아랩 학생'}
            </span>
            <span className="apple-work-cta">
              프로젝트 체험하기
              <FiArrowUpRight aria-hidden="true" />
            </span>
          </div>
        </div>
      </a>
    </article>
  );
};

const ProjectState = ({ status, onRetry }) => {
  if (status === 'loading') {
    return (
      <div className="apple-project-skeletons" role="status" aria-live="polite">
        <span className="apple-sr-only">학생 작품을 불러오는 중입니다.</span>
        {[0, 1, 2].map((item) => <div key={item} aria-hidden="true" />)}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="apple-project-state" role="alert">
        <FiRefreshCw aria-hidden="true" />
        <h3>작품을 불러오지 못했습니다.</h3>
        <p>잠시 후 다시 시도하면 지금 운영 중인 학생 작품을 확인할 수 있습니다.</p>
        <button type="button" onClick={onRetry}>
          다시 시도
          <FiArrowRight aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className="apple-project-state" role="status">
      <FiImage aria-hidden="true" />
      <h3>새로운 작품이 준비 중입니다.</h3>
      <p>학생들의 다음 아이디어가 공개되면 이곳에서 바로 체험할 수 있습니다.</p>
    </div>
  );
};

const HomePage = () => {
  const [projects, setProjects] = useState([]);
  const [projectStatus, setProjectStatus] = useState('loading');
  const [isShowcaseDragging, setIsShowcaseDragging] = useState(false);
  const [requestVersion, setRequestVersion] = useState(0);
  const journeyRef = useRef(null);
  const outcomeRef = useRef(null);
  const showcaseViewportRef = useRef(null);
  const showcaseTrackRef = useRef(null);
  const showcasePrimarySequenceRef = useRef(null);
  const showcaseDragRef = useRef({
    active: false,
    didDrag: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    startTime: 0,
    startAnimationTime: 0,
    animation: null,
    duration: 0,
    sequenceWidth: 0,
    suppressClickUntil: 0,
  });
  const prefersReducedMotion = useReducedMotion();
  const isCompactLayout = useCompactLayout();
  const isCoarsePointer = useMediaQuery(COARSE_POINTER_QUERY);
  const isShortOutcomeViewport = useMediaQuery(SHORT_OUTCOME_LAYOUT_QUERY);
  const { scrollYProgress: journeyProgress } = useScroll({
    target: journeyRef,
    offset: ['start start', 'end end'],
  });
  const smoothJourneyProgress = useSpring(journeyProgress, {
    stiffness: 92,
    damping: 25,
    mass: 0.35,
  });
  const journeyProgressScale = useTransform(smoothJourneyProgress, [0, 1], [0.06, 1]);
  const journeyConnectorScale = useTransform(
    smoothJourneyProgress,
    [0.08, 0.54],
    [0, 1],
  );
  const journeyKineticX = useTransform(
    smoothJourneyProgress,
    [0, 1],
    [80, -180],
  );
  const useStaticJourney = Boolean(prefersReducedMotion || isCompactLayout);
  const { scrollYProgress: outcomeProgress } = useScroll({
    target: outcomeRef,
    offset: ['start start', 'end end'],
  });
  const smoothOutcomeProgress = useSpring(outcomeProgress, {
    stiffness: 92,
    damping: 25,
    mass: 0.35,
  });
  const outcomeKineticX = useTransform(
    smoothOutcomeProgress,
    [0, 1],
    [140, -260],
  );
  const outcomeKineticY = useTransform(
    smoothOutcomeProgress,
    [0, 0.5, 1],
    [-4, 10, -2],
  );
  const outcomeKineticScale = useTransform(
    smoothOutcomeProgress,
    [0, 0.5, 1],
    [0.96, 1.025, 0.99],
  );
  const outcomeKineticOpacity = useTransform(
    smoothOutcomeProgress,
    [0, 0.08, 0.88, 1],
    [0.25, 1, 0.9, 0.48],
  );
  const outcomeThreadLength = useTransform(
    smoothOutcomeProgress,
    [0.06, 0.92],
    [0, 1],
  );
  const outcomeSignalOffset = useTransform(
    smoothOutcomeProgress,
    [0.04, 0.92],
    [0, -0.92],
  );
  const outcomeSignalOpacity = useTransform(
    smoothOutcomeProgress,
    [0.02, 0.05, 0.92, 0.97],
    [0, 1, 1, 0],
  );
  const outcomeFooterOpacity = useTransform(
    smoothOutcomeProgress,
    [0.84, 0.95],
    [0, 1],
  );
  const outcomeFooterY = useTransform(
    smoothOutcomeProgress,
    [0.84, 0.95],
    [18, 0],
  );
  const useStaticOutcome = Boolean(
    prefersReducedMotion || isCompactLayout || isShortOutcomeViewport,
  );
  const outcomeLayout = useStaticOutcome ? 'static' : 'sticky';
  const sectionIds = {
    belief: 'philosophy',
    experience: 'apple-experience',
    technology: 'about',
    showcase: 'projects',
    outcome: 'vision',
    contact: 'apple-contact',
  };

  useEffect(() => {
    document.documentElement.classList.add('home-page-active');

    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }

    return () => {
      document.documentElement.classList.remove('home-page-active');
    };
  }, []);

  useEffect(() => {
    let isCurrent = true;

    const loadProjects = async () => {
      setProjectStatus('loading');

      try {
        const response = await fetch(SHOWCASE_ENDPOINT, { cache: 'no-store' });
        if (!response.ok) throw new Error(`Showcase request failed: ${response.status}`);
        const payload = await response.json();
        const normalized = normalizeProjects(payload);

        if (isCurrent) {
          setProjects(normalized);
          setProjectStatus('ready');
        }
      } catch {
        if (isCurrent) {
          setProjects([]);
          setProjectStatus('error');
        }
      }
    };

    loadProjects();

    return () => {
      isCurrent = false;
    };
  }, [requestVersion]);

  const galleryProjects = useMemo(
    () => {
      if (projects.length === 0) return [];

      return Array.from(
        { length: Math.max(projects.length, MINIMUM_GALLERY_CARDS) },
        (_, visualIndex) => ({
          project: projects[visualIndex % projects.length],
          projectIndex: visualIndex % projects.length,
          isVisualRepeat: visualIndex >= projects.length,
        }),
      );
    },
    [projects],
  );

  useEffect(() => {
    if (
      !isCoarsePointer
      || projectStatus !== 'ready'
      || galleryProjects.length === 0
      || typeof window.requestAnimationFrame !== 'function'
    ) {
      return undefined;
    }

    const viewport = showcaseViewportRef.current;
    const primarySequence = showcasePrimarySequenceRef.current;
    const frame = viewport?.closest('.apple-film-frame');
    if (!viewport || !primarySequence || !frame) return undefined;

    let animationFrame = null;
    let resumeTimer = null;
    let lastFrameTime = null;
    let touchActive = false;

    const clearResumeTimer = () => {
      if (resumeTimer === null) return;
      window.clearTimeout(resumeTimer);
      resumeTimer = null;
    };

    const stopAutoplay = (state = 'paused') => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }
      lastFrameTime = null;
      frame.dataset.nativeAutoplayState = state;
    };

    const moveFilm = (timestamp) => {
      const sequenceWidth = primarySequence.scrollWidth;
      if (!sequenceWidth || touchActive) {
        stopAutoplay();
        return;
      }

      if (lastFrameTime !== null) {
        const elapsed = Math.min(32, Math.max(0, timestamp - lastFrameTime));
        viewport.scrollLeft += (
          SHOWCASE_NATIVE_AUTOPLAY_PX_PER_SECOND * elapsed
        ) / 1000;

        if (viewport.scrollLeft >= sequenceWidth) {
          viewport.scrollLeft -= sequenceWidth;
        }
      }

      lastFrameTime = timestamp;
      animationFrame = window.requestAnimationFrame(moveFilm);
    };

    const startAutoplay = () => {
      clearResumeTimer();
      if (touchActive || animationFrame !== null) return;

      frame.dataset.nativeAutoplayState = 'running';
      lastFrameTime = null;
      animationFrame = window.requestAnimationFrame(moveFilm);
    };

    const scheduleAutoplay = (delay = SHOWCASE_NATIVE_RESUME_DELAY_MS) => {
      clearResumeTimer();
      if (touchActive) return;

      frame.dataset.nativeAutoplayState = 'waiting';
      resumeTimer = window.setTimeout(() => {
        resumeTimer = null;
        startAutoplay();
      }, delay);
    };

    const handleTouchStart = () => {
      touchActive = true;
      clearResumeTimer();
      stopAutoplay();
    };

    const handleTouchEnd = () => {
      touchActive = false;
      scheduleAutoplay();
    };

    const handleNativeScroll = () => {
      if (animationFrame !== null || touchActive) return;
      scheduleAutoplay(SHOWCASE_NATIVE_SCROLL_IDLE_MS);
    };

    viewport.addEventListener('touchstart', handleTouchStart, { passive: true });
    viewport.addEventListener('touchend', handleTouchEnd, { passive: true });
    viewport.addEventListener('touchcancel', handleTouchEnd, { passive: true });
    viewport.addEventListener('scroll', handleNativeScroll, { passive: true });
    startAutoplay();

    return () => {
      clearResumeTimer();
      stopAutoplay('stopped');
      viewport.removeEventListener('touchstart', handleTouchStart);
      viewport.removeEventListener('touchend', handleTouchEnd);
      viewport.removeEventListener('touchcancel', handleTouchEnd);
      viewport.removeEventListener('scroll', handleNativeScroll);
      delete frame.dataset.nativeAutoplayState;
    };
  }, [galleryProjects.length, isCoarsePointer, projectStatus]);

  const getShowcaseAnimation = () => {
    const animations = showcaseTrackRef.current?.getAnimations?.() ?? [];
    return animations.find(
      (animation) => animation.animationName === 'apple-film-continuous-flow',
    ) ?? animations[0];
  };

  const normalizeShowcaseAnimationTime = (time, duration) => (
    ((time % duration) + duration) % duration
  );

  const handleShowcasePointerDown = (event) => {
    if (
      event.pointerType === 'touch'
      || isCoarsePointer
      || (event.pointerType === 'mouse' && event.button !== 0)
      || event.isPrimary === false
    ) {
      return;
    }

    const animation = getShowcaseAnimation();
    const sequenceWidth = showcasePrimarySequenceRef.current?.scrollWidth ?? 0;
    const computedDuration = Number(animation?.effect?.getComputedTiming?.().duration);

    if (!animation || !sequenceWidth || !Number.isFinite(computedDuration)) return;

    animation.play?.();

    showcaseDragRef.current = {
      ...showcaseDragRef.current,
      active: true,
      didDrag: false,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startTime: event.timeStamp,
      startAnimationTime: Number(animation.currentTime) || 0,
      animation,
      duration: computedDuration,
      sequenceWidth,
    };
  };

  const handleShowcasePointerMove = (event) => {
    const dragState = showcaseDragRef.current;
    if (!dragState.active || dragState.pointerId !== event.pointerId) return;

    const distanceX = event.clientX - dragState.startX;
    const distanceY = event.clientY - dragState.startY;

    if (!dragState.didDrag) {
      if (Math.abs(distanceX) < SHOWCASE_DRAG_THRESHOLD) return;
      if (Math.abs(distanceY) > Math.abs(distanceX)) return;
      dragState.didDrag = true;
      setIsShowcaseDragging(true);
      showcaseViewportRef.current?.setPointerCapture?.(event.pointerId);
    }

    event.preventDefault();
    const elapsed = Math.max(0, event.timeStamp - dragState.startTime);
    const draggedTime = (distanceX / dragState.sequenceWidth) * dragState.duration;
    const nextTime = dragState.startAnimationTime + elapsed - draggedTime;
    dragState.animation.currentTime = normalizeShowcaseAnimationTime(
      nextTime,
      dragState.duration,
    );
  };

  const finishShowcaseDrag = (event) => {
    const dragState = showcaseDragRef.current;
    if (!dragState.active || dragState.pointerId !== event.pointerId) return;

    if (dragState.didDrag) {
      dragState.suppressClickUntil = Date.now() + SHOWCASE_CLICK_SUPPRESSION_MS;
    }

    dragState.active = false;
    dragState.pointerId = null;
    dragState.animation = null;
    setIsShowcaseDragging(false);

    const viewport = showcaseViewportRef.current;
    if (
      viewport?.releasePointerCapture
      && (!viewport.hasPointerCapture || viewport.hasPointerCapture(event.pointerId))
    ) {
      viewport.releasePointerCapture(event.pointerId);
    }
  };

  const handleShowcaseClickCapture = (event) => {
    const dragState = showcaseDragRef.current;
    if (Date.now() >= dragState.suppressClickUntil) return;

    event.preventDefault();
    event.stopPropagation();
    dragState.suppressClickUntil = 0;
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="home-page">
        <Helmet>
          <title>NARNIA LAB — 아이디어를 실제 프로그램으로</title>
          <meta
            name="description"
            content="아이들이 해결하고 싶은 문제를 찾고, AI와 함께 프로그램을 만들어 실제 링크로 공개하는 프로젝트형 교육."
          />
        </Helmet>

        <a className="apple-skip-link" href="#apple-main">본문으로 바로가기</a>

      <main id="apple-main">
        <section className="apple-hero" aria-labelledby="apple-hero-title">
          <div className="apple-hero-glow apple-hero-glow--one" aria-hidden="true" />
          <div className="apple-hero-glow apple-hero-glow--two" aria-hidden="true" />
          <div className="apple-shell apple-hero-grid">
            <motion.div
              className="apple-hero-copy"
              variants={heroCopyVariants}
              initial={prefersReducedMotion ? false : 'hidden'}
              animate="visible"
            >
              <motion.span className="apple-eyebrow" variants={heroCopyItemVariants}>
                <i aria-hidden="true" />
                AI로 만드는 프로젝트 수업
              </motion.span>
              <motion.h1 id="apple-hero-title" variants={heroCopyItemVariants}>
                아이의 질문을,
                <br />
                <span>진짜 서비스로.</span>
              </motion.h1>
              <motion.p variants={heroCopyItemVariants}>
                나니아랩은 아이가 해결하고 싶은 문제를 찾고, AI와 함께 프로그램을 만들어
                실제 링크로 공개하는 프로젝트형 교육입니다.
              </motion.p>
              <motion.div className="apple-hero-actions" variants={heroCopyItemVariants}>
                <a className="apple-button apple-button--dark" href={FREE_TRIAL_APPLICATION_URL}>
                  무료 체험 신청하기
                  <FiArrowRight aria-hidden="true" />
                </a>
                <a
                  className="apple-text-link"
                  href={`#${sectionIds.showcase}`}
                >
                  학생 프로그램 보기
                  <FiArrowRight aria-hidden="true" />
                </a>
              </motion.div>
              <motion.div
                className="apple-hero-proof"
                aria-label="교육 특징"
                variants={heroCopyItemVariants}
              >
                <span><FiCompass aria-hidden="true" /> 문제 찾기</span>
                <span><FiCode aria-hidden="true" /> AI와 만들기</span>
                <span><FiArrowUpRight aria-hidden="true" /> 실제 링크로 공개</span>
              </motion.div>
            </motion.div>

            <div
              className="apple-hero-visual"
              role="img"
              aria-label="학생이 학교 축제의 긴 대기 줄 문제를 AI와 대화하며 정리하고, 부스별 대기 시간을 보여주는 서비스를 만든 뒤 안전한 공개 주소로 배포해 완성된 페이지를 브라우저에서 여는 과정"
            >
              <div className="apple-ai-ambient" aria-hidden="true">
                <i />
                <i />
                <i />
              </div>
              <motion.div
                className="apple-ai-dialogue"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 44, rotateX: 5, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, rotateX: 0, scale: 1 }}
                transition={{
                  type: 'spring',
                  stiffness: 105,
                  damping: 21,
                  mass: 0.85,
                  delay: 0.3,
                }}
                aria-hidden="true"
              >
                <header className="apple-ai-dialogue__bar">
                  <div className="apple-ai-window-dots">
                    <i />
                    <i />
                    <i />
                  </div>
                  <div className="apple-ai-identity">
                    <b>N</b>
                    <span>
                      NARNIA AI
                      <small>PROJECT STUDIO</small>
                    </span>
                  </div>
                  <div className="apple-ai-status">
                    <i />
                    함께 만드는 중
                  </div>
                </header>

                <div className="apple-ai-dialogue__body">
                  <div className="apple-ai-project-meta">
                    <span>PROJECT 01</span>
                    <strong>축제의 기다림을 바꾸는 아이디어</strong>
                  </div>

                  <div className="apple-ai-thread">
                    <article
                      className="apple-ai-message apple-ai-message--student"
                      data-ai-message="student"
                    >
                      <span>나</span>
                      <p>
                        학교 축제에서 줄이 너무 길어요.
                        <br />
                        대기시간을 알려주는 앱을 만들고 싶어요.
                      </p>
                    </article>

                    <div className="apple-ai-thinking">
                      <b>N</b>
                      <span><i /><i /><i /></span>
                    </div>

                    <article
                      className="apple-ai-message apple-ai-message--assistant"
                      data-ai-message="assistant"
                    >
                      <span>N</span>
                      <div>
                        <small>NARNIA AI</small>
                        <p>좋습니다. 친구들이 꼭 알아야 할 정보를 먼저 골라볼까요?</p>
                      </div>
                    </article>

                    <div className="apple-ai-choices">
                      <span>부스별 대기</span>
                      <span>혼잡도</span>
                      <span>즐겨찾기</span>
                    </div>
                  </div>

                  <aside className="apple-ai-preview" data-ai-preview="service">
                    <header>
                      <span><i /> LIVE PREVIEW</span>
                      <strong>축제 NOW</strong>
                    </header>
                    <div className="apple-ai-preview__booths">
                      <div>
                        <span>과학 체험</span>
                        <strong>5분</strong>
                      </div>
                      <div>
                        <span>쿠킹 클래스</span>
                        <strong>12분</strong>
                      </div>
                      <div>
                        <span>보드게임</span>
                        <strong>여유</strong>
                      </div>
                    </div>
                    <footer>
                      <span><i /> 미리보기 완성</span>
                      <FiArrowUpRight />
                    </footer>
                  </aside>

                  <aside className="apple-ai-deploy" data-ai-deploy="service">
                    <header>
                      <span>DEPLOYMENT</span>
                      <small><i /> BUILD → HTTPS → LIVE</small>
                    </header>
                    <div className="apple-ai-deploy__pipeline">
                      <div>
                        <b>✓</b>
                        <span>BUILD<small>화면·기능 검사</small></span>
                      </div>
                      <i />
                      <div>
                        <b>✓</b>
                        <span>HTTPS<small>보안 주소 연결</small></span>
                      </div>
                      <i />
                      <div>
                        <b>✓</b>
                        <span>LIVE<small>서비스 공개</small></span>
                      </div>
                    </div>
                    <div className="apple-ai-deploy__progress"><i /></div>
                    <div className="apple-ai-deploy__url">
                      <div>
                        <small>PUBLIC URL</small>
                        <strong>festival-now.narnialab.app</strong>
                      </div>
                      <span><i /> ONLINE</span>
                    </div>
                    <footer>
                      <span>배포가 끝났어요. 이제 친구들에게 링크를 보낼 수 있어요.</span>
                      <FiArrowUpRight />
                    </footer>
                  </aside>

                  <aside
                    className="apple-ai-service-window"
                    data-ai-service-window="live"
                  >
                    <header className="apple-ai-service-window__chrome">
                      <span className="apple-ai-service-window__dots">
                        <i />
                        <i />
                        <i />
                      </span>
                      <div className="apple-ai-service-window__address">
                        <i aria-hidden="true" />
                        <span>festival-now.narnialab.app</span>
                      </div>
                      <span className="apple-ai-service-window__live">
                        <i />
                        LIVE
                      </span>
                    </header>

                    <div className="apple-ai-service-page">
                      <div className="apple-ai-service-page__nav">
                        <strong>축제 NOW</strong>
                        <span>오늘의 축제</span>
                        <i><b /> 실시간 운영 중</i>
                      </div>

                      <div className="apple-ai-service-page__hero">
                        <div>
                          <span>우리 학교 축제 · 실시간</span>
                          <h3>
                            기다리지 말고,
                            <br />
                            <em>지금 즐겨요.</em>
                          </h3>
                          <p>부스별 대기 시간을 한눈에 확인하세요.</p>
                        </div>
                        <div className="apple-ai-service-page__signal">
                          <i />
                          <i />
                          <i />
                          <strong>12</strong>
                          <span>LIVE BOOTHS</span>
                        </div>
                      </div>

                      <div className="apple-ai-service-page__booths">
                        <article>
                          <span>SCIENCE</span>
                          <strong>과학 체험실</strong>
                          <p><i /> 여유 <b>5분</b></p>
                        </article>
                        <article>
                          <span>COOKING</span>
                          <strong>쿠킹 클래스</strong>
                          <p><i /> 보통 <b>12분</b></p>
                        </article>
                        <article>
                          <span>GAME</span>
                          <strong>보드게임존</strong>
                          <p><i /> 바로 입장 <b>0분</b></p>
                        </article>
                      </div>

                      <div className="apple-ai-service-page__ticker">
                        <span><i /> LIVE UPDATE</span>
                        <strong>로봇 축구 대기 시간이 3분 줄었어요.</strong>
                        <small>방금</small>
                      </div>
                    </div>
                  </aside>

                  <div className="apple-ai-composer">
                    <span>
                      <i />
                      AI와 다음 선택을 이야기해 보세요
                    </span>
                    <b><FiArrowUpRight /></b>
                  </div>
                </div>
              </motion.div>

              <div className="apple-ai-caption" aria-hidden="true">
                <FiStar />
                <span>아이의 한마디가, 작동하는 서비스로.</span>
              </div>
            </div>
          </div>
          <div className="apple-hero-foot apple-shell" aria-hidden="true">
            <span>SCROLL TO DISCOVER</span>
            <i />
            <span>NARNIA LAB · 2026</span>
          </div>
        </section>

        <section
          className="apple-journey"
          id={sectionIds.belief}
          ref={journeyRef}
          aria-labelledby="apple-belief-title"
        >
          {!isCompactLayout && (
            <span
              className="apple-section-anchor apple-section-anchor--experience"
              id={sectionIds.experience}
              aria-hidden="true"
            />
          )}
          <div className="apple-journey__sticky">
            <div className="apple-journey__glow apple-journey__glow--one" aria-hidden="true" />
            <div className="apple-journey__glow apple-journey__glow--two" aria-hidden="true" />

            <div className="apple-shell apple-journey__inner">
              <motion.div
                className="apple-journey__heading"
                initial={prefersReducedMotion ? false : { opacity: 0, x: -70, y: 18 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true, amount: 0.55 }}
                transition={{ duration: 0.78, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="apple-section-label">
                  <span>NARNIA METHOD</span>
                  <i aria-hidden="true" />
                  <p>FROM QUESTION TO LIVE</p>
                </div>
                <div className="apple-journey__title-row">
                  <h2 id="apple-belief-title">
                    질문은 AI와 만나,
                    <br />
                    <span>작동하는 서비스가 됩니다.</span>
                  </h2>
                  <p>
                    아이가 고른 문제를 AI와 만들고, 누구나 접속할 수 있는 실제 링크로
                    공개합니다.
                  </p>
                </div>
              </motion.div>

              <div className="apple-journey__kinetic" aria-hidden="true">
                <motion.div style={useStaticJourney ? undefined : { x: journeyKineticX }}>
                  <span>QUESTION</span>
                  <i />
                  <span>BUILD</span>
                  <i />
                  <span>LIVE</span>
                </motion.div>
              </div>

              <div
                className="apple-card-stage"
                id={isCompactLayout ? sectionIds.experience : undefined}
                aria-label="질문부터 공개까지 이어지는 세 단계 프로젝트 수업"
              >
                <div className="apple-card-connector" aria-hidden="true">
                  <motion.b style={{ scaleX: journeyConnectorScale }} />
                  {[0, 1, 2].map((index) => (
                    <JourneyConnectorNode
                      index={index}
                      key={index}
                      progress={smoothJourneyProgress}
                    />
                  ))}
                </div>
                {experienceCards.map((card, index) => (
                  <JourneyCard
                    card={card}
                    index={index}
                    key={card.number}
                    progress={smoothJourneyProgress}
                    staticLayout={useStaticJourney}
                  />
                ))}
              </div>

              <div className="apple-journey__footer">
                <p>
                  <span aria-hidden="true" />
                  PROJECT BASED LEARNING
                  <b>질문 → 제작 → 공개</b>
                </p>
                <div className="apple-journey__progress" aria-hidden="true">
                  <span>QUESTION</span>
                  <i>
                    <motion.b style={{ scaleX: journeyProgressScale }} />
                  </i>
                  <span>LIVE</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          className="apple-technology"
          id={sectionIds.technology}
          aria-labelledby="apple-technology-title"
        >
          <div className="apple-tech-glow apple-tech-glow--one" aria-hidden="true" />
          <div className="apple-tech-glow apple-tech-glow--two" aria-hidden="true" />

          <div className="apple-shell">
            <motion.header
              className="apple-technology-head"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 44 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.45 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <div>
                <span className="apple-tech-label"><i aria-hidden="true" /> CORE TECHNOLOGY</span>
                <h2 id="apple-technology-title">
                  최신 AI 세 가지를,
                  <br />
                  <em>아이에게는 하나의 쉬운 작업실로.</em>
                </h2>
              </div>
              <p>
                Claude, Gemini, ChatGPT를 한 화면에서 선택합니다. 복잡한 설치와 명령어 대신
                초등학생도 폴더를 고르고 말로 요청하며 프로그램을 완성할 수 있습니다.
              </p>
            </motion.header>

            <div className="apple-technology-stage">
              <motion.div
                className="apple-technology-copy"
                initial={prefersReducedMotion ? false : { opacity: 0, x: -42 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.78, ease: [0.22, 1, 0.36, 1] }}
              >
                <span>NARNIA AI WORKSPACE</span>
                <h3>복잡한 기술은 안쪽에.<br />만드는 즐거움은 앞쪽에.</h3>
                <p>
                  나니아는 AI 대화, 파일 탐색, 실행 환경, 작품 공유를 하나의 프로젝트
                  맥락으로 연결합니다. 아이는 도구를 옮겨 다니지 않고 생각과 결과에
                  집중합니다.
                </p>

                <div className="apple-tech-simple-flow" aria-label="나니아 사용 흐름">
                  <span><b>1</b> 폴더 선택</span>
                  <i aria-hidden="true" />
                  <span><b>2</b> AI와 대화</span>
                  <i aria-hidden="true" />
                  <span><b>3</b> 실행·공유</span>
                </div>
              </motion.div>

              <motion.div
                className="apple-tech-window"
                data-narnia-product="workspace"
                role="img"
                aria-label="나니아 제품에서 Claude, Gemini, ChatGPT를 선택하고 프로젝트 폴더의 파일을 보며 AI와 대화해 프로그램을 실행하는 화면"
                initial={prefersReducedMotion ? false : { opacity: 0, x: 52, scale: 0.97 }}
                whileInView={{ opacity: 1, x: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.88, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="apple-tech-window__beam" aria-hidden="true" />
                <header className="apple-tech-window__bar">
                  <span className="apple-tech-window__dots" aria-hidden="true">
                    <i /><i /><i />
                  </span>
                  <div>
                    <FiFolder aria-hidden="true" />
                    <span>/projects/my-first-service</span>
                  </div>
                  <small><i aria-hidden="true" /> WORKSPACE CONNECTED</small>
                </header>

                <div className="apple-tech-window__body">
                  <aside className="apple-tech-explorer" aria-hidden="true">
                    <header>
                      <span><FiFolder /> 탐색기</span>
                      <small>PROJECT</small>
                    </header>
                    <div className="apple-tech-file is-open">
                      <span><i /> my-first-service</span>
                    </div>
                    <div className="apple-tech-file is-active">
                      <span><i /> index.html</span>
                      <small>HTML</small>
                    </div>
                    <div className="apple-tech-file">
                      <span><i /> style.css</span>
                      <small>CSS</small>
                    </div>
                    <div className="apple-tech-file">
                      <span><i /> app.js</span>
                      <small>JS</small>
                    </div>
                    <footer>
                      <FiTerminal />
                      <span>AI가 현재 폴더를<br />함께 보고 있어요.</span>
                    </footer>
                  </aside>

                  <div className="apple-tech-studio">
                    <header className="apple-tech-studio__head">
                      <div>
                        <span>AI 선택</span>
                        <strong>어떤 AI와도 같은 방식으로</strong>
                      </div>
                      <span className="apple-tech-resume"><i /> RESUME READY</span>
                    </header>

                    <div className="apple-tech-models" aria-label="사용 가능한 AI">
                      <span className="is-gemini"><i>G</i> Gemini <b>READY</b></span>
                      <span className="is-claude"><i>C</i> Claude <b>READY</b></span>
                      <span className="is-chatgpt"><i>◎</i> ChatGPT <b>READY</b></span>
                    </div>

                    <div className="apple-tech-dialogue">
                      <article className="is-student">
                        <span>나</span>
                        <p>학교 축제 대기시간 앱을 만들고 싶어. 친구들이 바로 이해하게 해줘.</p>
                      </article>
                      <article className="is-ai">
                        <span>N</span>
                        <div>
                          <small>NARNIA AI · 현재 폴더에서 작업 중</small>
                          <p>좋습니다. 가장 필요한 화면부터 만들고 바로 실행해서 확인해 볼게요.</p>
                          <div className="apple-tech-task">
                            <span><FiCheck /> 화면 구성</span>
                            <span><FiCheck /> 기능 연결</span>
                            <span className="is-working"><i /> 미리보기 실행</span>
                          </div>
                        </div>
                      </article>
                    </div>

                    <div className="apple-tech-result">
                      <div>
                        <span>LIVE PREVIEW</span>
                        <strong>축제 NOW</strong>
                        <p><i /> 실행 중 · 안전한 HTTPS</p>
                      </div>
                      <div>
                        <span>PUBLIC LINK</span>
                        <strong>my-first-service</strong>
                        <p>작품전시관에 공유 준비 완료 <FiArrowUpRight /></p>
                      </div>
                    </div>

                    <div className="apple-tech-composer">
                      <span>다음 아이디어를 말해보세요</span>
                      <i aria-hidden="true" />
                      <b><FiArrowUpRight /></b>
                    </div>
                  </div>
                </div>

                <footer className="apple-tech-window__status">
                  <span><i /> MAIN AI</span>
                  <span><i /> AI DOCK</span>
                  <span><i /> FILE EXPLORER</span>
                  <strong>한 화면에서 모두 연결됨</strong>
                </footer>
              </motion.div>
            </div>

            <motion.div
              className="apple-tech-feature-grid"
              role="list"
              aria-label="초등학생을 위한 나니아 핵심 기능"
              variants={observableListVariants}
              initial={prefersReducedMotion ? false : 'hidden'}
              whileInView="visible"
              viewport={{ once: true, amount: 0.14 }}
            >
              {technologyFeatures.map((feature) => {
                const Icon = feature.icon;
                return (
                  <motion.article key={feature.number} role="listitem" variants={observableItemVariants}>
                    <header>
                      <span>{feature.number}</span>
                      <small>{feature.eyebrow}</small>
                      <i><Icon aria-hidden="true" /></i>
                    </header>
                    <h3>{feature.title}</h3>
                    <p>{feature.copy}</p>
                  </motion.article>
                );
              })}
            </motion.div>
          </div>
        </section>

        <section
          className="apple-showcase"
          id={sectionIds.showcase}
          aria-labelledby="apple-showcase-title"
        >
          <div className="apple-shell">
            <motion.div
              className="apple-showcase-head"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 42 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.55 }}
              transition={{ duration: 0.78, ease: [0.22, 1, 0.36, 1] }}
            >
              <div>
                <span className="apple-showcase-label">STUDENT PROJECTS</span>
                <h2 id="apple-showcase-title">학생들의 프로그램이<br />지금, 작동합니다.</h2>
              </div>
              <div className="apple-showcase-copy">
                <p>
                  설명보다 결과가 먼저입니다. 초등 고학년과 중학생을 중심으로, 학생들이
                  직접 만들고 공개한 프로그램을 지금 체험해 보세요.
                </p>
              </div>
            </motion.div>

            {projectStatus === 'ready' && projects.length > 0 ? (
              <div
                className={`apple-film-frame${isShowcaseDragging ? ' is-dragging' : ''}`}
                data-gallery-direction="right-to-left"
                data-project-count={projects.length}
                data-autoplay={isCoarsePointer ? 'native-scroll' : 'continuous'}
                data-drag-state={isShowcaseDragging ? 'dragging' : 'idle'}
                style={{
                  '--apple-showcase-duration': `${galleryProjects.length * SHOWCASE_SECONDS_PER_CARD}s`,
                }}
              >
                <div className="apple-film-toolbar">
                  <div>
                    <span>NARNIA SHOWCASE</span>
                    <strong>{String(projects.length).padStart(2, '0')} PROJECTS</strong>
                  </div>
                  <a className="apple-film-toolbar__cta" href="/showcase">
                    작품 더보기
                    <FiArrowRight aria-hidden="true" />
                  </a>
                </div>

                <div
                  className="apple-film-viewport"
                  id="apple-student-project-gallery"
                  ref={showcaseViewportRef}
                  role="region"
                  aria-label={isCoarsePointer
                    ? `자동으로 흐르며 좌우로 스와이프할 수 있는 학생 작품 ${projects.length}개 갤러리`
                    : `자동으로 흐르며 좌우로 드래그할 수 있는 학생 작품 ${projects.length}개 갤러리`}
                  aria-roledescription={isCoarsePointer
                    ? '자동 이동 및 터치 스크롤 가능한 가로 갤러리'
                    : '자동 순환 및 드래그 가능한 가로 갤러리'}
                  onPointerDown={handleShowcasePointerDown}
                  onPointerMove={handleShowcasePointerMove}
                  onPointerUp={finishShowcaseDrag}
                  onPointerCancel={finishShowcaseDrag}
                  onLostPointerCapture={finishShowcaseDrag}
                  onClickCapture={handleShowcaseClickCapture}
                  onDragStart={(event) => event.preventDefault()}
                >
                  <div className="apple-film-track" ref={showcaseTrackRef}>
                    <div
                      className="apple-film-sequence apple-film-sequence--primary"
                      ref={showcasePrimarySequenceRef}
                      role="list"
                      aria-label="현재 전시 중인 학생 작품"
                    >
                      {galleryProjects.map(({
                        project,
                        projectIndex,
                        isVisualRepeat,
                      }, visualIndex) => (
                        <WorkCard
                          key={`primary-${project.id ?? project.launch_url}-${visualIndex}`}
                          project={project}
                          index={projectIndex}
                          isDuplicate={isVisualRepeat}
                        />
                      ))}
                    </div>
                    <div
                      className="apple-film-sequence apple-film-sequence--duplicate"
                      aria-hidden="true"
                    >
                      {galleryProjects.map(({ project, projectIndex }, visualIndex) => (
                        <WorkCard
                          key={`duplicate-${project.id ?? project.launch_url}-${visualIndex}`}
                          project={project}
                          index={projectIndex}
                          isDuplicate
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="apple-film-caption">
                  <div
                    className="apple-showcase-note"
                    role="note"
                    aria-label="학생 작품 안내"
                  >
                    <span className="apple-showcase-note__icon" aria-hidden="true">
                      <FiRefreshCw />
                    </span>
                    <p>
                      <strong>이 작품들은 지금도 계속 발전하는 중입니다.</strong>
                      <span>
                        전시된 모습은 완성본이 아닙니다. 아이들이 새로운 아이디어와 피드백을
                        더하며 계속 고치고 확장해 가는 프로젝트입니다.
                      </span>
                    </p>
                  </div>
                  <small>
                    <FiMove aria-hidden="true" />
                    카드를 누르면 체험하고, 좌우로 끌거나 스와이프하면 작품을 둘러볼 수 있어요.
                  </small>
                </div>
              </div>
            ) : (
              <ProjectState
                status={projectStatus}
                onRetry={() => setRequestVersion((version) => version + 1)}
              />
            )}
          </div>
        </section>

        <section
          className="apple-outcome"
          id={sectionIds.outcome}
          ref={outcomeRef}
          aria-labelledby="apple-outcome-title"
          data-outcome-layout={outcomeLayout}
        >
          <div className="apple-outcome__sticky" key={outcomeLayout}>
            <div className="apple-shell">
              <motion.header
                className="apple-outcome-head"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 44 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.28 }}
                transition={{ duration: 0.78, ease: [0.22, 1, 0.36, 1] }}
              >
                <div>
                  <span>WHAT REMAINS</span>
                  <h2 id="apple-outcome-title">결과물과 함께,<br /><em>생각하는 힘이 남습니다.</em></h2>
                </div>
                <p>
                  코딩 문법을 외우는 데서 끝나지 않습니다. 문제를 정의하고, 선택을 설명하고,
                  피드백으로 다시 고치는 경험이 아이의 것이 됩니다.
                </p>
              </motion.header>

              <motion.div
                className="apple-thinking-trace"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 46, scale: 0.985 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.08 }}
                transition={{ duration: 0.86, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="apple-thinking-trace__kinetic" aria-hidden="true">
                  <motion.div
                    style={useStaticOutcome ? undefined : {
                      x: outcomeKineticX,
                      y: outcomeKineticY,
                      scale: outcomeKineticScale,
                      opacity: outcomeKineticOpacity,
                    }}
                  >
                    <span>THINKING</span>
                    <i />
                    <span>TRACE</span>
                    <i />
                    <span>THINKING</span>
                  </motion.div>
                </div>

                <svg
                  className="apple-thinking-thread"
                  viewBox="0 0 1200 360"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <path
                    className="apple-thinking-thread__base"
                    d="M80 256 C250 52 355 304 520 178 S820 40 1118 160"
                  />
                  <motion.path
                    className="apple-thinking-thread__draw"
                    d="M80 256 C250 52 355 304 520 178 S820 40 1118 160"
                    style={useStaticOutcome ? undefined : { pathLength: outcomeThreadLength }}
                    initial={useStaticOutcome ? { pathLength: 0 } : false}
                    whileInView={useStaticOutcome ? { pathLength: 1 } : undefined}
                    viewport={useStaticOutcome ? { once: true, amount: 0.4 } : undefined}
                    transition={useStaticOutcome
                      ? { duration: 1.45, ease: [0.22, 1, 0.36, 1] }
                      : undefined}
                  />
                  {!useStaticOutcome && (
                    <motion.path
                      className="apple-thinking-thread__signal"
                      d="M80 256 C250 52 355 304 520 178 S820 40 1118 160"
                      pathLength="1"
                      strokeDasharray="0.1 0.9"
                      style={{
                        strokeDashoffset: outcomeSignalOffset,
                        opacity: outcomeSignalOpacity,
                      }}
                    />
                  )}
                </svg>

                <div
                  className="apple-thinking-moments"
                  role="list"
                  aria-label="프로젝트 뒤에 남는 세 가지 변화"
                >
                  {observableChanges.map((change, index) => (
                    <OutcomeCard
                      change={change}
                      index={index}
                      key={change.eyebrow}
                      progress={smoothOutcomeProgress}
                      staticLayout={useStaticOutcome}
                    />
                  ))}
                </div>

                <motion.footer
                  className="apple-thinking-trace__footer"
                  style={useStaticOutcome ? undefined : {
                    opacity: outcomeFooterOpacity,
                    y: outcomeFooterY,
                  }}
                >
                  <span>프로그램 하나보다 더 오래 남는 변화</span>
                  <strong>생각 <i /> 시도 <i /> 공유</strong>
                </motion.footer>
              </motion.div>
            </div>
          </div>
        </section>

        <section
          className="apple-final"
          id={sectionIds.contact}
          aria-labelledby="apple-final-title"
        >
          <div className="apple-final-orb" aria-hidden="true" />
          <div className="apple-final-atmosphere" aria-hidden="true">
            <div className="apple-final-orbit apple-final-orbit--outer">
              <i />
            </div>
            <div className="apple-final-orbit apple-final-orbit--inner">
              <i />
            </div>
            <span className="apple-final-particle apple-final-particle--one" />
            <span className="apple-final-particle apple-final-particle--two" />
            <span className="apple-final-particle apple-final-particle--three" />
            <article className="apple-final-signal apple-final-signal--question">
              <span>QUESTION · 01</span>
              <strong>축제의 긴 대기 줄을<br />바꾸고 싶어요.</strong>
              <i>아이의 문제 발견</i>
            </article>
            <article className="apple-final-signal apple-final-signal--live">
              <span>PUBLIC PROJECT</span>
              <strong>festival-now</strong>
              <i><b /> LIVE · 안전한 링크</i>
            </article>
          </div>
          <motion.div
            className="apple-shell apple-final-inner"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.82, ease: [0.22, 1, 0.36, 1] }}
          >
            <span>READY TO BUILD</span>
            <h2 id="apple-final-title">아이의 첫 프로그램,<br />나니아랩에서 시작하세요.</h2>
            <p>수업 방식과 시작 방법을 편하게 상담해 드립니다.</p>
            <div className="apple-final-actions">
              <a
                className="apple-button apple-button--light"
                href={FREE_TRIAL_APPLICATION_URL}
              >
                무료 체험 신청하기
                <FiArrowRight aria-hidden="true" />
              </a>
              <a
                className="apple-button apple-button--glass"
                href="/education"
              >
                교육소개 보기
                <FiArrowRight aria-hidden="true" />
              </a>
              <a
                className="apple-button apple-button--glass"
                href="https://pf.kakao.com/_DZhmn"
                target="_blank"
                rel="noopener noreferrer"
              >
                교육 상담하기
                <FiArrowUpRight aria-hidden="true" />
              </a>
            </div>
          </motion.div>
        </section>
      </main>

      </div>
    </MotionConfig>
  );
};

export default HomePage;
